import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import math from '../../services/expression';
import Abilities from './Abilities';
import T, { Translate } from '../i18n/i18n';
import { Section, ActionButtonWrapper, ActionButton, ExtraActions } from './Characters.styles';
import { Container, FormCharRow, CharAvatar, FormCharStack, FormCharLabel, FormCharColor, FormCharInput, FormCharSelect, Warning, FormCharEmptyLabel, ViewTabList, ViewTab, ViewContent, ButtonBar, TextArea, CopyInput } from './FormChar.styles';
import { getChar, updateChar, removeChar, duplicateChar } from '../../services/chars';
import { rtcBufferActions, charactersActions, filemanagerActions } from '../../redux/actions';
import ResourceImage from '../ResourceImage/ResourceImage';
import Keyboard from '../../services/keyboardShortcuts';
import Checkbox from '../Forms/Checkbox/Checkbox';


export default ({charId, charsList, onClose, setCharsList, tableId}) => {

    const dispatch = useDispatch();
    const [formId, setFormId] = useState(null);
    const [formName, setFormName] = useState('');
    const [formAvatar, setFormAvatar] = useState('');
    const [formPlayer, setFormPlayer] = useState('');
    const [formParentChar, setFormParentChar] = useState(null);
    const [formRedMax, setFormRedMax] = useState('');
    const [formRedCurrent, setFormRedCurrent] = useState('');
    const [formRedExpression, setFormRedExpression] = useState('');
    const [formRedVisibility, setFormRedVisibility] = useState('');
    const [formGreenMax, setFormGreenMax] = useState('');
    const [formGreenCurrent, setFormGreenCurrent] = useState('');
    const [formGreenExpression, setFormGreenExpression] = useState('');
    const [formGreenVisibility, setFormGreenVisibility] = useState('');
    const [formBlueMax, setFormBlueMax] = useState('');
    const [formBlueCurrent, setFormBlueCurrent] = useState('');
    const [formBlueExpression, setFormBlueExpression] = useState('');
    const [formBlueVisibility, setFormBlueVisibility] = useState('');
    const [formSizeX, setFormSizeX] = useState(1);
    const [formSizeY, setFormSizeY] = useState(1);
    const [formHideName, setFormHideName] = useState(false);
    const [formBio, setFormBio] = useState('');
    const [formNotes, setFormNotes] = useState('');
    const [formAbilities, setFormAbilities] = useState([]);
    const [formTags, setFormTags] = useState('');
    const [numOfCopies, setNumOfCopies] = useState(1);
    const [isOpenedExtraOps, setIsOpenedExtraOps] = useState(false);
    const [isConfirmRemove, setIsConfirmRemove] = useState(false);
    const [isConfirmDuplicate, setIsConfirmDuplicate] = useState(false);
    const [error, setError] = useState('');
    const [viewMode, setViewMode] = useState('status');
    const [shouldClose, setShouldCLose] = useState(false);
    const [sheetValues, setSheetValues] = useState({});

    const selectedSheet = useSelector(state => state.charactersState.sheettemplate);
    const participants = useSelector(state => state.playersState.participants);
    const updatedCharId = useSelector(state => state.charactersState.updatedCharId);
    const updatedTimestamp = useSelector(state => state.charactersState.updatedTimestamp);
    const isOwner = useSelector(state => state.authState.isOwner);
    const playerId = useSelector(state => state.authState.id);

    const copyField = useRef(null);
    const valueOnFocus = useRef({});
    const shoulUpdateCurrent = useRef(null);
    

    const loadCharData = useCallback(async (isOwner, playerId, tableId, charId) => {
        if (charId) {
            let char = await getChar(tableId, charId) || {};

            if (!char.id || (!isOwner && char.player !== playerId)) {
                markToClose();
                return;
            }

            try {
                setSheetValues(JSON.parse(char.sheetValues || '{}') || {});
            } catch (e) {
                console.log(e);
                setSheetValues({});
            }

            setFormId(char.id);
            setFormName(char.name || '');
            setFormHideName(char.hideName || false);
            setFormAvatar(char.avatar || '');
            setFormPlayer(char.player || '');
            setFormParentChar(char.parentChar || null);
            setFormRedMax(char.red_max || '');
            setFormRedCurrent(char.red_current || '');
            setFormRedVisibility(char.red_visibility || '');
            setFormBlueMax(char.blue_max || '');
            setFormBlueCurrent(char.blue_current || '');
            setFormBlueVisibility(char.blue_visibility || '');
            setFormGreenMax(char.green_max || '');
            setFormGreenCurrent(char.green_current || '');
            setFormGreenVisibility(char.green_visibility || '');
            setFormSizeX(char.sizeX || 1);
            setFormSizeY(char.sizeY || 1);
            setFormBio(char.bio || '');
            setFormNotes(char.notes || '');
            setFormAbilities(JSON.parse(char.abilities || '[]'));
            setFormTags(char.tags || '');
        }
    }, []);

    useEffect(() => {
        if (isConfirmDuplicate && copyField.current) {
            copyField.current.focus();
        }
    }, [isConfirmDuplicate]);

    useEffect(() => {
        setShouldCLose(false);
        loadCharData(isOwner, playerId, tableId, charId);
    }, [tableId, charId, isOwner, playerId, loadCharData]);

    useEffect(() => {
        if (updatedTimestamp !== null && updatedCharId === charId && charId !== null) {
            loadCharData(isOwner, playerId, tableId, charId);
        }
    }, [tableId, charId, isOwner, playerId, updatedCharId, updatedTimestamp, loadCharData]);

    useEffect(() => {
        if (shouldClose) {
            onClose();
        }
    }, [shouldClose, onClose]);

    useEffect(() => {
        setFormRedExpression(formRedCurrent);
        setFormGreenExpression(formGreenCurrent);
        setFormBlueExpression(formBlueCurrent);
    }, [formRedCurrent, formGreenCurrent, formBlueCurrent]);


    const clearForm = useCallback(() => {
        setFormId(null);
        setFormName('');
        setFormHideName(false);
        setFormAvatar('');
        setFormPlayer('');
        setFormParentChar(null);
        setFormRedMax('');
        setFormRedCurrent('');
        setFormRedVisibility('');
        setFormGreenMax('');
        setFormGreenCurrent('');
        setFormGreenVisibility('');
        setFormBlueMax('');
        setFormBlueCurrent('');
        setFormBlueVisibility('');
        setFormSizeX(1);
        setFormSizeY(1);
        setFormBio('');
        setFormNotes('');
        setFormAbilities([]);
        setFormTags('');
    }, []);

    const markToClose = () => {
        setShouldCLose(true);
    }

    const handleCancelForm = () => {
        clearForm();
        onClose();
    }

    const performSave = useCallback(async (overrideValues = {}) => {
        let playerName = null;
        let selectedPlayer = participants.filter(player => player._id === formPlayer);
        if (selectedPlayer.length > 0) {
            playerName = selectedPlayer[0].name;
        }

        let charData = {
            id: overrideValues.id || formId || null,
            name: overrideValues.name || formName,
            hideName: overrideValues.hideName ?? formHideName,
            avatar: overrideValues.avatar || formAvatar,
            playerId: overrideValues.playerId || formPlayer,
            playerName: overrideValues.playerName || playerName,
            redMax: overrideValues.redMax || formRedMax,
            redCurrent: overrideValues.redCurrent || formRedCurrent,
            redVisibility: overrideValues.redVisibility || formRedVisibility,
            greenMax: overrideValues.greenMax || formGreenMax,
            greenCurrent: overrideValues.greenCurrent || formGreenCurrent,
            greenVisibility: overrideValues.greenVisibility || formGreenVisibility,
            blueMax: overrideValues.blueMax || formBlueMax,
            blueCurrent: overrideValues.blueCurrent || formBlueCurrent,
            blueVisibility: overrideValues.blueVisibility || formBlueVisibility,
            sizeX: overrideValues.sizeX || formSizeX,
            sizeY: overrideValues.sizeY || formSizeY,
            bio: overrideValues.bio || formBio,
            notes: overrideValues.notes || formNotes,
            abilities: overrideValues.abilities || JSON.stringify(formAbilities),
            tags: overrideValues.tags || formTags
        };

        let char = await updateChar(tableId, charData);

        if (char) {
            let charToPropagate = {...char};
            delete charToPropagate.bio;
            delete charToPropagate.notes;

            dispatch(rtcBufferActions.pushOutputBuffer({
                type: 'charUpdateSync',
                data: {id: formId, char: charToPropagate}
            }));
            dispatch(charactersActions.updateChar(formId, charToPropagate, true));
            setCharsList((chars) => chars.map((i) => i.id !== formId ? i : char));
        } else {
            setError(char.payload);
        }
    },[dispatch, formAbilities, formAvatar, formBio, formBlueCurrent, formBlueMax, formBlueVisibility, formGreenCurrent, formGreenMax, formGreenVisibility, formId, formName, formHideName, formNotes, formPlayer, formRedCurrent, formRedMax, formRedVisibility, formSizeX, formSizeY, formTags, participants, setCharsList, tableId]);

    useEffect(() => {
        if (shoulUpdateCurrent.current) {
            let opts = shoulUpdateCurrent.current;
            performSave({[opts[0]]: opts[1]});
            shoulUpdateCurrent.current = false;
        }
    }, [performSave, formRedCurrent, formGreenCurrent, formBlueCurrent]);

    const handleRemoveChar = async () => {
        await removeChar(tableId, formId);

        dispatch(rtcBufferActions.pushOutputBuffer({
            type: 'charRemoveSync',
            data: {id: formId}
        }));
        dispatch(charactersActions.removeChar(formId, true));
        setCharsList(chars => [...chars].filter(i => i.id !== formId));
        setIsOpenedExtraOps(false);
        clearForm();
        markToClose();
    };

    const selectAvatarImage = () => {
        dispatch(filemanagerActions.openWindow('single', async (result) => {
            setFormAvatar(result[0].id);
            performSave({avatar: result[0].id});
        }));
    }

    const handleOpenSheet = () => {
        dispatch(charactersActions.setSheetFormOpened(true, formParentChar || formId));
    }

    const handleOpenBio = () => {
        setViewMode('bio');
    }

    const handleOpenAbilities = () => {
        setViewMode('abilities');
    }

    const handleOpenStatus = () => {
        setViewMode('status');
    }

    const updateSizeX = (val) => {
        setFormSizeX(parseInt(val.replace(/[^\d]/gi,'')));
    }

    const updateSizeY = (val) => {
        setFormSizeY(parseInt(val.replace(/[^\d]/gi,'')));
    }

    const handleChangeAbilities = (val) => {
        setFormAbilities(val);
        performSave({abilities: JSON.stringify(val)});
    }

    const handleChangeTags = (val) => {
        let validValue = val.replace(/[^0-9a-zA-Z-]/gi,' ');
        setFormTags(validValue);
    }

    const handleOpenCopy = () => {
        if (!formParentChar) {
            setIsConfirmDuplicate(true);
        }
    }

    const handleDuplicateChar = async () => {
        if (formParentChar) {
            return;
        }

        let res = await duplicateChar(tableId, formId, numOfCopies);
        let childrenChars = res.payload;
        
        if (!Array.isArray(childrenChars)) {
            setError(childrenChars);
            return;
        }

        dispatch(rtcBufferActions.pushOutputBuffer({
            type: 'charCopySync',
            data: {id: formId, chars: childrenChars}
        }));
        dispatch(charactersActions.updateChildrenChar(formId, childrenChars, true));
        setCharsList(chars => {
            let newCharList = [...chars].filter(i => i.parentChar !== formId);
            return [...newCharList, ...childrenChars];
        });

        setIsOpenedExtraOps(false);
        clearForm();
        markToClose();
    }

    const updateValueUsingExpression = (value, current, setFn, prop) => {
        if (/^[+-]/gi.test(String(value))) {
            let valAsFloat = String(current).replace(/[^()\d.+-]/gi, '') || '0';
            let result = math.evaluate(valAsFloat + '' + value);
            setFn((old) => {
                let ret = result >= 0 ? result : `(${result})`
                if (old !== ret) {
                    shoulUpdateCurrent.current = [prop, ret];
                }
                return ret;
            });
        } else {
            setFn((old) => {
                let ret = value || '';
                if (old !== ret) {
                    shoulUpdateCurrent.current = [prop, ret];
                }
                return ret;
            });
        }
    }

    const selectValue = (e) => {
        e.target.select();
    }

    const handleKeyPress = async (e, current, set, prop) => {
        if (String(e.key).toUpperCase() !== 'ENTER') {
            return;
        }

        updateValueUsingExpression(e.target.value, current, set, prop);

        e.preventDefault();
        e.stopPropagation();
    }

    const startDraggingChar = (event) => {
        event.dataTransfer.setData("text/plain", charId);
        event.dataTransfer.effectAllowed = "link";
        event.dataTransfer.dropEffect = "link";
    };

    useEffect(() => {
        let escapeKey = () => {
            handleCancelForm(charId);
            return false;
        };
    
        Keyboard.register({key:'ESCAPE'}, escapeKey);
        
        return () => {
            Keyboard.unregister({key:'ESCAPE'}, escapeKey);
        }
    }, [handleCancelForm, charId]);

    return <Container >
        {error && <Warning>{error}</Warning>}
        <FormCharRow>
            <CharAvatar 
                onClick={() => {selectAvatarImage()}}
                draggable="true" 
                className='draggable'
                onDragStart={(e)=>{startDraggingChar(e)}} 
            >
                <ResourceImage resourceId={formAvatar} />
            </CharAvatar>
            <FormCharStack>
                <FormCharLabel><T value="characters.name"/></FormCharLabel>
                <FormCharRow>
                    <FormCharInput tabIndex="1" value={formName} onFocus={()=>{valueOnFocus.current['formName'] = formName}} onChange={(e)=>{setFormName(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formName']!==formName){performSave()}}} className="withMargin fullWidth" />
                    {isOwner ? <Checkbox width={100} onClick={(val) => { setFormHideName(val); performSave({hideName: val});}} value={formHideName}>
                        <FormCharLabel><T value="characters.unamed"/></FormCharLabel>
                    </Checkbox> : null }
                </FormCharRow>
                <FormCharRow>
                    <FormCharStack>
                        <FormCharLabel><T value="characters.player"/></FormCharLabel>
                        <FormCharSelect tabIndex="2" value={formPlayer} onFocus={()=>{valueOnFocus.current['formPlayer'] = formPlayer}} onChange={(e)=>{setFormPlayer(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formPlayer']!==formPlayer){performSave()}}} className="fullWidth" type="text">
                            <option value=""></option>
                            {participants && participants.length && participants.map((i, k) => <option key={k} value={i._id}>{i.name}</option>)}
                        </FormCharSelect>
                    </FormCharStack>
                    <FormCharStack>
                        <FormCharLabel><T value="characters.tags"/></FormCharLabel>
                        <FormCharInput tabIndex="3" value={formTags} onFocus={()=>{valueOnFocus.current['formTags'] = formTags}} onChange={(e)=>{handleChangeTags(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formTags']!==formTags){performSave()}}} className="fullWidth" />
                    </FormCharStack>
                </FormCharRow>
            </FormCharStack>
        </FormCharRow>
        <FormCharRow>
            {selectedSheet && selectedSheet.id ? <ActionButtonWrapper>
                <ActionButton color="#fc2" onClick={handleOpenSheet}><T value={formParentChar ? "characters.openOriginalSheet" : "characters.openSheet"}/></ActionButton>
            </ActionButtonWrapper> : <Warning>
                <T value="characters.noSheet"/>
            </Warning>}
        </FormCharRow>
        <FormCharRow>
            <ViewTabList>
                <ViewTab className={viewMode === 'status' ? 'active' : ''}onClick={handleOpenStatus}><T value="characters.openStatus"/></ViewTab>
                <ViewTab className={viewMode === 'bio' ? 'active' : ''}onClick={handleOpenBio}><T value="characters.openBio"/></ViewTab>
                <ViewTab className={viewMode === 'abilities' ? 'active' : ''}onClick={handleOpenAbilities}><T value="characters.openAbilities"/></ViewTab>
            </ViewTabList>
        </FormCharRow>
        {viewMode === 'status' && <ViewContent>
            <FormCharRow>
                <FormCharStack className="small">
                    <FormCharEmptyLabel />
                    <FormCharColor color={'#f33'} />
                    <FormCharColor color={'#0c0'} />
                    <FormCharColor color={'#03f'} />
                </FormCharStack>
                <FormCharStack>
                    <FormCharLabel><T value="characters.current"/></FormCharLabel>
                    <FormCharInput tabIndex="4" value={formRedExpression} onFocus={(e)=>selectValue(e)} onChange={(e)=>{setFormRedExpression(e.target.value)}} onBlur={(e)=>{updateValueUsingExpression(e.target.value, formRedCurrent, setFormRedCurrent, 'formRedCurrent')}} onKeyPress={(e) => {handleKeyPress(e, formRedCurrent, setFormRedCurrent, 'formRedCurrent')}}type="text"/>
                    <FormCharInput tabIndex="7" value={formGreenExpression} onFocus={(e)=>selectValue(e)} onChange={(e)=>{setFormGreenExpression(e.target.value)}} onBlur={(e)=>{updateValueUsingExpression(e.target.value, formGreenCurrent, setFormGreenCurrent, 'formGreenCurrent')}} onKeyPress={(e) => {handleKeyPress(e, formGreenCurrent, setFormGreenCurrent, 'formGreenCurrent')}}type="text"/>
                    <FormCharInput tabIndex="10" value={formBlueExpression} onFocus={(e)=>selectValue(e)} onChange={(e)=>{setFormBlueExpression(e.target.value)}} onBlur={(e)=>{updateValueUsingExpression(e.target.value, formBlueCurrent, setFormBlueCurrent, 'formBlueCurrent')}} onKeyPress={(e) => {handleKeyPress(e, formBlueCurrent, setFormBlueCurrent, 'formBlueCurrent')}}type="text"/>
                </FormCharStack>
                <FormCharStack>
                    <FormCharLabel><T value="characters.max"/></FormCharLabel>
                    <FormCharInput tabIndex="5" value={formRedMax} onFocus={()=>{valueOnFocus.current['formRedMax'] = formRedMax}} onChange={(e)=>{setFormRedMax(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formRedMax']!==formRedMax){performSave()}}} type="text"/>
                    <FormCharInput tabIndex="8" value={formGreenMax} onFocus={()=>{valueOnFocus.current['formGreenMax'] = formGreenMax}} onChange={(e)=>{setFormGreenMax(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formGreenMax']!==formGreenMax){performSave()}}} type="text"/>
                    <FormCharInput tabIndex="11" value={formBlueMax} onFocus={()=>{valueOnFocus.current['formBlueMax'] = formBlueMax}} onChange={(e)=>{setFormBlueMax(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formBlueMax']!==formBlueMax){performSave()}}} type="text"/>
                </FormCharStack>
                <FormCharStack className="big">
                    <FormCharLabel><T value="characters.visibility"/></FormCharLabel>
                    <FormCharSelect tabIndex="6" value={formRedVisibility} onFocus={()=>{valueOnFocus.current['formRedVisibility'] = formRedVisibility}} onChange={(e)=>{setFormRedVisibility(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formRedVisibility']!==formRedVisibility){performSave()}}}>
                        <option value="">{Translate("characters.showNone")}</option>
                        <option value="bar">{Translate("characters.showBar")}</option>
                        <option value="single">{Translate("characters.showSingle")}</option>
                        <option value="value">{Translate("characters.showValue")}</option>
                    </FormCharSelect>
                    <FormCharSelect tabIndex="9" value={formGreenVisibility} onFocus={()=>{valueOnFocus.current['formGreenVisibility'] = formGreenVisibility}} onChange={(e)=>{setFormGreenVisibility(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formGreenVisibility']!==formGreenVisibility){performSave()}}}>
                        <option value="">{Translate("characters.showNone")}</option>
                        <option value="bar">{Translate("characters.showBar")}</option>
                        <option value="single">{Translate("characters.showSingle")}</option>
                        <option value="value">{Translate("characters.showValue")}</option>
                    </FormCharSelect>
                    <FormCharSelect tabIndex="12" value={formBlueVisibility} onFocus={()=>{valueOnFocus.current['formBlueVisibility'] = formBlueVisibility}} onChange={(e)=>{setFormBlueVisibility(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formBlueVisibility']!==formBlueVisibility){performSave()}}}>
                        <option value="">{Translate("characters.showNone")}</option>
                        <option value="bar">{Translate("characters.showBar")}</option>
                        <option value="single">{Translate("characters.showSingle")}</option>
                        <option value="value">{Translate("characters.showValue")}</option>
                    </FormCharSelect>
                </FormCharStack>
            </FormCharRow>
            <FormCharRow>
                <FormCharStack className="wide">
                    <FormCharEmptyLabel />
                    <FormCharLabel className="big" height="30px"><T value={"characters.labelsize"} /></FormCharLabel>
                </FormCharStack>
                <FormCharStack>
                    <FormCharLabel><T value={"characters.labelwidth"} /></FormCharLabel>
                    <FormCharInput tabIndex="13" value={formSizeX} onFocus={()=>{valueOnFocus.current['formSizeX'] = formSizeX}} onChange={(e)=>{updateSizeX(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formSizeX']!==formSizeX){performSave()}}} type="text"/>
                </FormCharStack>
                <FormCharStack>
                    <FormCharLabel><T value={"characters.labelheight"} /></FormCharLabel>
                    <FormCharInput tabIndex="14" value={formSizeY} onFocus={()=>{valueOnFocus.current['formSizeY'] = formSizeY}} onChange={(e)=>{updateSizeY(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formSizeY']!==formSizeY){performSave()}}} type="text"/>
                </FormCharStack>
            </FormCharRow>
        </ViewContent>}
        {viewMode === 'bio' && <ViewContent>
            <FormCharLabel><T value={"characters.labelbio"} /></FormCharLabel>
            <TextArea className="scrollbar" value={formBio} onFocus={()=>{valueOnFocus.current['formBio'] = formBio}} onChange={(e) => {setFormBio(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formBio']!==formBio){performSave()}}}></TextArea>
            <FormCharLabel><T value={"characters.labelnotes"} /></FormCharLabel>
            <TextArea className="scrollbar" value={formNotes} onFocus={()=>{valueOnFocus.current['formNotes'] = formNotes}} onChange={(e) => {setFormNotes(e.target.value)}} onBlur={()=>{if(valueOnFocus.current['formNotes']!==formNotes){performSave()}}}></TextArea>
        </ViewContent>}
        {viewMode === 'abilities' && <ViewContent className="scrollbar">
            <Abilities charId={formId} sheetValues={sheetValues} value={formAbilities} onChange={(val) => {handleChangeAbilities(val)}} />
        </ViewContent>}
        <ButtonBar>
            {!isOpenedExtraOps && <ActionButtonWrapper>
                <ActionButton color="#F44" onClick={handleCancelForm}><T value="back"/></ActionButton>
                {isOwner && <ActionButton color="#04F" width="30px" onClick={() => {setIsOpenedExtraOps(true)}}><ExtraActions icon={"\\F01D8"}/></ActionButton>}
            </ActionButtonWrapper>}
            {isOpenedExtraOps && !isConfirmRemove && !isConfirmDuplicate && <ActionButtonWrapper>
                <ActionButton className={formParentChar ? 'disabled' : ''} color="#F92" onClick={() => {handleOpenCopy()}}><T value="characters.duplicate"/></ActionButton>
                <ActionButton color="#F44" onClick={() => {setIsConfirmRemove(true)}}><T value="delete"/></ActionButton>
                <ActionButton color="#04F" width="30px" onClick={() => {setIsOpenedExtraOps(false)}}><ExtraActions icon={"\\F0156"}/></ActionButton>
            </ActionButtonWrapper>}
            {isOpenedExtraOps && isConfirmRemove && !isConfirmDuplicate && <>
                <ActionButtonWrapper>
                    <ActionButton color="#0c0" onClick={() => {handleRemoveChar()}}><T value="yes"/></ActionButton>
                    <ActionButton color="#F44" onClick={() => {setIsConfirmRemove(false)}}><T value="no"/></ActionButton>
                </ActionButtonWrapper>
                <Section className="confirmationLabel"><T value="characters.confirmRemove"/></Section>
            </>}
            {isOpenedExtraOps && !isConfirmRemove && isConfirmDuplicate && <>
                <Section className="confirmationLabel spaceshared"><T value="characters.confirmDuplicate"/></Section>
                <CopyInput ref={copyField} value={numOfCopies} onChange={(e)=>{setNumOfCopies(e.target.value)}} type="text"/>
                <ActionButtonWrapper className="spaceshared">
                    <ActionButton color="#0c0" onClick={() => {handleDuplicateChar()}}><T value="characters.copy"/></ActionButton>
                    <ActionButton color="#F44" onClick={() => {setIsConfirmDuplicate(false)}}><T value="cancel"/></ActionButton>
                </ActionButtonWrapper>
            </>}
        </ButtonBar>
    </Container>
}